import { Button, TextField, Thumbnail, VideoThumbnail } from "matsuri-ui"
import {
    Loading,
    Paper,
    Typography,
    resetButtonStyle,
    useAlert
} from "matsuri-ui"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import { css } from "@emotion/react"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import CloseIcon from "@mui/icons-material/Close"
import UploadIcon from "@mui/icons-material/Upload"
import { theme } from "../../theme"
import { sequence } from "../../utils/sequence"
import {
    completePhotoTourImage,
    requestDeletePhotoTourImage,
    requestDeletePhotoTourMovie,
    requestPutPhotoTourImage,
    requestUpdateUploadedPhotoTourImage,
    requestUpdateUploadedPhotoTourMovie
} from "../../hooks/usePhotoTour"
import {
    genCleaningTourPagePath,
    genOperationReportPagePath
} from "../../routes/protected"
import { assertIsDefined } from "../../utils/assetIsDefined"
import { PhotoPageLayout } from "../../templates/PhotoPageLayout"
import { usePhotoTourContext } from "./providers/photoTour"
import { useTourMedia } from "@/hooks/useTourMedia"
import { useOperation } from "@/hooks/useOperaion"
import {
    completePhotoTourMovie,
    requestPutPhotoTourMovie
} from "@/hooks/useMovie"
import { imageCompress } from "@/utils/image"
import { BackToDetailConfirmDialog } from "@/components/Dialog/BackToDetailConfirmDialog"
import { useNavigateBackToDetail } from "@/hooks/useNavigateBackToDetail"
import { useTranslation } from "@/hooks/useTranslation"
// 選択時 PUT の同時実行上限（圧縮/転送の同時スパイク抑制。タスク写真側の throttle と同値）
const PUT_CONCURRENCY = 5

export type UploadStatus = "uploading" | "putDone" | "error"

export interface Picture {
    id: string
    file: File
    description: string
    uploadId?: string
    status: UploadStatus
    // 選択中サムネイルのプレビュー用。圧縮済み Blob の objectURL を保持する。
    // 生ファイル（カメラ撮影の高解像度 JPEG など）をそのまま <img> でデコードすると
    // 低スペック端末でメモリ不足によりデコードに失敗し壊れて表示されるため、縮小済みを表示する。
    previewUrl?: string
}

export interface Movie {
    id: string
    file: File
    description: string
    uploadId?: string
    status: UploadStatus
}

const styles = {
    footerButton: css`
        display: grid;
        justify-content: center;
        padding: 8px 0;
        text-align: center;
        border-radius: 6px;
        border-radius: 6px;
    `,
    disabled: css`
        opacity: 0.5;
    `,
    uploadedThumbnailWrapper: css`
        position: relative;
    `,
    deleteButton: css`
        all: unset;
        position: absolute;
        top: -8px;
        right: -8px;
        z-index: 2;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 4px;
        color: white;
        cursor: pointer;
        background-color: ${theme.palette.intentions.secondary.main};
        border-radius: 50%;

        &:hover {
            background-color: ${theme.palette.intentions.secondary.dark};
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.5;
        }
    `
}

const MobileFooterIconButton = ({
    icon,
    label,
    disabled,
    ...props
}: {
    icon: React.ReactNode
    label: string
    disabled?: boolean
} & React.ComponentPropsWithoutRef<"button">) => {
    return (
        <button
            css={[
                resetButtonStyle,
                styles.footerButton,
                disabled && styles.disabled
            ]}
            {...props}
            disabled={disabled}
        >
            <div
                css={css`
                    display: grid;
                    place-items: center;
                `}
            >
                {icon}
            </div>
            <div>
                <Typography>{label}</Typography>
            </div>
        </button>
    )
}

// 選択中サムネイルに PUT 状態（アップロード中 / 失敗）を重ねて表示する
const UploadStatusOverlay = ({ status }: { status: UploadStatus }) => {
    const { t } = useTranslation()
    if (status === "putDone") return null

    if (status === "uploading") {
        return (
            <div
                css={css`
                    position: absolute;
                    inset: 0;
                    z-index: 1;
                    display: grid;
                    place-items: center;
                    background-color: rgba(0, 0, 0, 0.35);
                `}
            >
                <Loading
                    css={css`
                        font-size: 28px;
                        color: white;
                    `}
                />
            </div>
        )
    }

    return (
        <div
            css={css`
                position: absolute;
                inset: 0;
                z-index: 1;
                display: grid;
                place-items: center;
                font-size: 12px;
                color: white;
                background-color: rgba(200, 0, 0, 0.55);
            `}
        >
            {t("送信失敗")}
        </div>
    )
}

const ItemPageInner = ({ index }: { index: number }) => {
    const { cleaningId, items, tour, sampleImagesMap } = usePhotoTourContext()
    const item = items?.[index]

    const uploadFileInputRef = useRef<HTMLInputElement>(null)
    const [picturesToUpload, setPicturesToUpload] = useState<Picture[]>([])
    const [moviesToUpload, setMoviesToUpload] = useState<Movie[]>([])

    // 即時アップロード用: 選択時に走らせた PUT の Promise / 中断用 AbortController を id で管理する。
    const uploadPromisesRef = useRef<Map<string, Promise<void>>>(new Map())
    const abortControllersRef = useRef<Map<string, AbortController>>(new Map())
    // PUT 結果を id で同期的に記録する。送信時に state(picturesRef) の反映待ちに依存せず参照するため。
    const putResultsRef = useRef<
        Map<string, { uploadId?: string; failed: boolean }>
    >(new Map())
    // プレビュー用 objectURL を id で同期的に保持する。revoke を effect 同期の
    // picturesRef に依存させると、setState 直後〜effect 反映前の削除/離脱で取り逃すため。
    const previewUrlsRef = useRef<Map<string, string>>(new Map())
    const idCounterRef = useRef(0)
    const genId = () => `${idCounterRef.current++}`

    // 送信時に最新の選択状態を参照するための ref
    const picturesRef = useRef<Picture[]>([])
    const moviesRef = useRef<Movie[]>([])
    useEffect(() => {
        picturesRef.current = picturesToUpload
        moviesRef.current = moviesToUpload
    }, [picturesToUpload, moviesToUpload])

    // アンマウント時（アイテム移動・離脱）に進行中の PUT を中断し、
    // アンマウント後の state 更新・不要な通信を防ぐ。
    useEffect(() => {
        const abortControllers = abortControllersRef.current
        const promises = uploadPromisesRef.current
        const putResults = putResultsRef.current
        const previewUrls = previewUrlsRef.current
        return () => {
            abortControllers.forEach(ac => ac.abort())
            abortControllers.clear()
            promises.clear()
            putResults.clear()
            // 選択中プレビューの objectURL を解放する（リーク防止）。
            // effect 同期の picturesRef ではなく同期更新の ref から解放する。
            previewUrls.forEach(url => URL.revokeObjectURL(url))
            previewUrls.clear()
        }
    }, [])

    const { token } = useAuth()
    const { throwAlert, addAlert } = useAlert()
    const navigate = useNavigate()
    const { t } = useTranslation()

    // Pre-fetch translated strings for event handlers
    const confirmSkipText = t("写真、動画を送信せず、次に進みますか？")
    const confirmEndTourText = t(
        "ツアーを終了して、次のセクションに進みますか？"
    )
    const uploadErrorText = t("アップロード中にエラーが発生しました")
    const uploadSuccessText = t("アップロードが完了しました")

    // ツアー詳細画面に戻る確認モーダル
    const {
        confirmBackOpen,
        openConfirmBack,
        closeConfirmBack,
        goBackToDetail
    } = useNavigateBackToDetail("operation", cleaningId)

    const [uploading, setUploading] = useState(0)
    const [uploaded, setUploaded] = useState(0)
    const waitingUpload = uploading > 0
    const isHandlingUploadRef = useRef(false)

    const { data: operation } = useOperation(token, cleaningId)
    const { data: media, refetch: refetchMedia } = useTourMedia(
        token,
        cleaningId
    )

    const [deletedImageIds, setDeletedImageIds] = useState<Set<string>>(
        new Set()
    )
    const [deletedMovieIds, setDeletedMovieIds] = useState<Set<string>>(
        new Set()
    )

    // cleaningId が切り替わった場合に削除済み Set をリセットする
    const [prevCleaningId, setPrevCleaningId] = useState(cleaningId)
    if (cleaningId !== prevCleaningId) {
        setPrevCleaningId(cleaningId)
        setDeletedImageIds(new Set())
        setDeletedMovieIds(new Set())
    }

    // 削除リクエスト中の id 集合(画像/動画それぞれ)
    const [deletingImageIds, setDeletingImageIds] = useState<Set<string>>(
        new Set()
    )
    const [deletingMovieIds, setDeletingMovieIds] = useState<Set<string>>(
        new Set()
    )

    // アンマウント後の setState 警告/メモリリークを防ぐ
    const isMountedRef = useRef(true)
    useEffect(() => {
        isMountedRef.current = true
        return () => {
            isMountedRef.current = false
        }
    }, [])

    const handleDeleteImage = useCallback(
        async (imageId: string) => {
            if (deletingImageIds.has(imageId)) return
            setDeletingImageIds(prev => new Set(prev).add(imageId))
            const { err } = await requestDeletePhotoTourImage(
                token,
                cleaningId,
                imageId
            )
            if (err) {
                if (!isMountedRef.current) return
                addAlert(t("写真の削除に失敗しました"), {
                    severity: "error",
                    duration: 2500
                })
                setDeletingImageIds(prev => {
                    const next = new Set(prev)
                    next.delete(imageId)
                    return next
                })
                return
            }
            // 成功時は再マウント時の不整合を防ぐため SWR キャッシュを必ず更新する
            // (アンマウント後でも mutate は安全に呼べる)
            void refetchMedia()
            if (!isMountedRef.current) return
            setDeletedImageIds(prev => new Set(prev).add(imageId))
        },
        [addAlert, cleaningId, deletingImageIds, refetchMedia, t, token]
    )

    const handleDeleteMovie = useCallback(
        async (movieId: string) => {
            if (deletingMovieIds.has(movieId)) return
            setDeletingMovieIds(prev => new Set(prev).add(movieId))
            const { err } = await requestDeletePhotoTourMovie(
                token,
                cleaningId,
                movieId
            )
            if (err) {
                if (!isMountedRef.current) return
                addAlert(t("動画の削除に失敗しました"), {
                    severity: "error",
                    duration: 2500
                })
                setDeletingMovieIds(prev => {
                    const next = new Set(prev)
                    next.delete(movieId)
                    return next
                })
                return
            }
            void refetchMedia()
            if (!isMountedRef.current) return
            setDeletedMovieIds(prev => new Set(prev).add(movieId))
        },
        [addAlert, cleaningId, deletingMovieIds, refetchMedia, t, token]
    )

    const filteredMovies = useMemo(
        () =>
            media?.movies?.filter(
                movie =>
                    movie.photoTourItemId === item?.id &&
                    !deletedMovieIds.has(movie.id)
            ) ?? [],
        [media?.movies, item?.id, deletedMovieIds]
    )

    const filteredPictures = useMemo(
        () =>
            media?.images?.filter(
                image =>
                    image.photoTourItemId === item?.id &&
                    !deletedImageIds.has(image.id)
            ) ?? [],
        [media?.images, item?.id, deletedImageIds]
    )

    const sampleImages = useMemo(() => {
        if (!item || !sampleImagesMap) {
            return []
        }

        return sampleImagesMap[item.id] || []
    }, [item, sampleImagesMap])

    const updatePicture = useCallback((id: string, patch: Partial<Picture>) => {
        setPicturesToUpload(prev =>
            prev.map(p => (p.id === id ? { ...p, ...patch } : p))
        )
    }, [])
    const updateMovie = useCallback((id: string, patch: Partial<Movie>) => {
        setMoviesToUpload(prev =>
            prev.map(m => (m.id === id ? { ...m, ...patch } : m))
        )
    }, [])

    // 選択時 PUT の同時実行数を制限する（圧縮/転送の同時スパイクを抑える）。
    const activePutCountRef = useRef(0)
    const putQueueRef = useRef<(() => void)[]>([])
    const pumpPutQueue = useCallback(() => {
        while (
            activePutCountRef.current < PUT_CONCURRENCY &&
            putQueueRef.current.length > 0
        ) {
            const job = putQueueRef.current.shift()
            if (!job) break
            activePutCountRef.current++
            job()
        }
    }, [])
    // work を同時実行上限のもとで実行し、完了時に解決する Promise を返す。
    const schedulePut = useCallback(
        (work: () => Promise<void>): Promise<void> =>
            new Promise<void>(resolve => {
                putQueueRef.current.push(() => {
                    work().finally(() => {
                        activePutCountRef.current = Math.max(
                            0,
                            activePutCountRef.current - 1
                        )
                        resolve()
                        pumpPutQueue()
                    })
                })
                pumpPutQueue()
            }),
        [pumpPutQueue]
    )

    // 選択と同時に圧縮 + issue + S3 PUT(pending) を先行実行する（同時実行は制限）。
    const startImagePut = useCallback(
        (picture: Picture) => {
            if (!item) return
            const ac = new AbortController()
            abortControllersRef.current.set(picture.id, ac)
            const promise = schedulePut(async () => {
                try {
                    if (ac.signal.aborted) return
                    const compressed = await imageCompress(picture.file, {
                        quality: 0.85,
                        maxWidth: 1500,
                        maxHeight: 1500
                    })
                    if (ac.signal.aborted) return
                    // 選択中プレビューは生ファイルではなく圧縮済み Blob を表示する
                    // （生の高解像度画像は低スペック端末で <img> デコードに失敗し壊れるため）。
                    // revoke 取り逃し防止のため objectURL は ref にも同期的に記録する。
                    const previewUrl = URL.createObjectURL(compressed)
                    previewUrlsRef.current.set(picture.id, previewUrl)
                    updatePicture(picture.id, { previewUrl })
                    const { uploadId } = await requestPutPhotoTourImage(
                        token,
                        cleaningId,
                        item.id,
                        compressed,
                        ac.signal
                    )
                    putResultsRef.current.set(picture.id, {
                        uploadId,
                        failed: false
                    })
                    updatePicture(picture.id, { uploadId, status: "putDone" })
                } catch (error) {
                    if (
                        ac.signal.aborted ||
                        (error as { name?: string })?.name === "AbortError"
                    ) {
                        return
                    }
                    putResultsRef.current.set(picture.id, { failed: true })
                    updatePicture(picture.id, { status: "error" })
                }
            })
            uploadPromisesRef.current.set(picture.id, promise)
        },
        [cleaningId, item, token, updatePicture, schedulePut]
    )

    const startMoviePut = useCallback(
        (movie: Movie) => {
            if (!item) return
            const ac = new AbortController()
            abortControllersRef.current.set(movie.id, ac)
            const promise = schedulePut(async () => {
                try {
                    if (ac.signal.aborted) return
                    const { uploadId } = await requestPutPhotoTourMovie(
                        token,
                        cleaningId,
                        item.id,
                        movie.file,
                        ac.signal
                    )
                    putResultsRef.current.set(movie.id, {
                        uploadId,
                        failed: false
                    })
                    updateMovie(movie.id, { uploadId, status: "putDone" })
                } catch (error) {
                    if (
                        ac.signal.aborted ||
                        (error as { name?: string })?.name === "AbortError"
                    ) {
                        return
                    }
                    putResultsRef.current.set(movie.id, { failed: true })
                    updateMovie(movie.id, { status: "error" })
                }
            })
            uploadPromisesRef.current.set(movie.id, promise)
        },
        [cleaningId, item, token, updateMovie, schedulePut]
    )

    const removePicture = useCallback((id: string) => {
        abortControllersRef.current.get(id)?.abort()
        abortControllersRef.current.delete(id)
        uploadPromisesRef.current.delete(id)
        putResultsRef.current.delete(id)
        // ref から同期的に revoke（effect 同期の picturesRef には依存しない）。
        const previewUrl = previewUrlsRef.current.get(id)
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl)
            previewUrlsRef.current.delete(id)
        }
        setPicturesToUpload(prev => prev.filter(p => p.id !== id))
    }, [])
    const removeMovie = useCallback((id: string) => {
        abortControllersRef.current.get(id)?.abort()
        abortControllersRef.current.delete(id)
        uploadPromisesRef.current.delete(id)
        putResultsRef.current.delete(id)
        setMoviesToUpload(prev => prev.filter(m => m.id !== id))
    }, [])

    const handleUpload = async () => {
        if (isHandlingUploadRef.current) {
            return
        }
        isHandlingUploadRef.current = true
        try {
            await handleUploadInner()
        } finally {
            isHandlingUploadRef.current = false
        }
    }

    const handleUploadInner = async () => {
        const next = () => {
            navigate(
                genCleaningTourPagePath(cleaningId, `photo/item/${index + 1}`)
            )
        }

        if (!item) {
            return
        }

        const totalSelected =
            picturesRef.current.length + moviesRef.current.length

        if (totalSelected === 0) {
            const ok = window.confirm(confirmSkipText)
            if (!ok) {
                return
            }

            next()
            return
        }

        setUploading(totalSelected)
        setUploaded(0)

        // 選択時に開始した PUT がまだ進行中なら完了を待つ
        await Promise.allSettled([...uploadPromisesRef.current.values()])

        // picturesRef は説明文の参照に使う（PUT 結果は putResultsRef から同期的に読む。
        // state 反映の commit 待ちに依存しないため、選択直後の送信でも取りこぼさない）
        const images = picturesRef.current
        const videos = moviesRef.current

        // PUT 失敗（または結果欠落）が残っている場合は送信を中断し、再選択を促す
        const isItemFailed = (id: string) => {
            const result = putResultsRef.current.get(id)
            return !result || result.failed
        }
        if (
            images.some(p => isItemFailed(p.id)) ||
            videos.some(m => isItemFailed(m.id))
        ) {
            setUploading(0)
            setUploaded(0)
            throwAlert(new Error(uploadErrorText), {
                errorMessage: uploadErrorText
            })
            return
        }

        const errors: unknown[] = []

        // PUT 済み画像を complete し、説明があれば更新する
        await sequence(
            images.map(image => async () => {
                const uploadId = putResultsRef.current.get(image.id)?.uploadId
                if (!uploadId) {
                    errors.push(new Error("uploadId missing"))
                    setUploaded(prev => prev + 1)
                    return
                }
                const { data, error } = await completePhotoTourImage(
                    token,
                    cleaningId,
                    item.id,
                    uploadId
                )
                if (error) {
                    errors.push(error)
                }

                // 空文字ではない場合だけ送信
                if (image.description?.trim() && data?.id) {
                    const { error: updateDescriptionError } =
                        await requestUpdateUploadedPhotoTourImage(
                            token,
                            data.id,
                            image.description
                        )
                    if (updateDescriptionError) {
                        errors.push(updateDescriptionError)
                    }
                }

                setUploaded(prev => prev + 1)
            })
        )

        await sequence(
            videos.map(video => async () => {
                const uploadId = putResultsRef.current.get(video.id)?.uploadId
                if (!uploadId) {
                    errors.push(new Error("uploadId missing"))
                    setUploaded(prev => prev + 1)
                    return
                }
                const { data, error } = await completePhotoTourMovie(
                    token,
                    cleaningId,
                    item.id,
                    uploadId
                )

                if (error) {
                    errors.push(error)
                }

                if (video.description && data?.id) {
                    const { error: updateDescriptionError } =
                        await requestUpdateUploadedPhotoTourMovie(
                            token,
                            data.id,
                            video.description
                        )
                    if (updateDescriptionError) {
                        errors.push(updateDescriptionError)
                    }
                }

                setUploaded(prev => prev + 1)
            })
        )

        throwAlert(errors.length > 0 ? new Error(uploadErrorText) : undefined, {
            errorMessage: uploadErrorText,
            successMessage: uploadSuccessText,
            onSuccess: next,
            onError: () => {
                setUploading(0)
                setUploaded(0)
            }
        })
    }

    // ツアーが終了したら、次のevidenceのセクションに進む
    if (items && index >= items?.length) {
        return (
            <Navigate
                to={genOperationReportPagePath(
                    cleaningId,
                    "ready-for-troubles"
                )}
            />
        )
    }

    return (
        <>
            {/* ツアー詳細画面に戻る確認モーダル */}
            <BackToDetailConfirmDialog
                type="operation"
                open={confirmBackOpen}
                onClose={closeConfirmBack}
                onConfirm={goBackToDetail}
            />
            <PhotoPageLayout
                headerTitle={t(`${tour?.title}のツアー`)}
                onNext={openConfirmBack}
                navigationButtons={
                    <div
                        css={css`
                            display: flex;
                            column-gap: 8px;
                            justify-content: space-between;
                        `}
                    >
                        <Button
                            disabled={index === 0 || waitingUpload}
                            onClick={() => {
                                if (index === 0) return
                                if (
                                    picturesToUpload.length !== 0 ||
                                    moviesToUpload.length !== 0
                                ) {
                                    handleUpload()
                                    return
                                }
                                navigate(
                                    genCleaningTourPagePath(
                                        cleaningId,
                                        `photo/item/${index - 1}`
                                    )
                                )
                            }}
                            variant="outlined"
                            color="primary"
                            icon={<ArrowBackIcon />}
                        >
                            {t("戻る")}
                        </Button>

                        <Button
                            disabled={waitingUpload}
                            onClick={() => {
                                if (
                                    items?.length &&
                                    index >= items?.length - 1
                                ) {
                                    const ok =
                                        window.confirm(confirmEndTourText)

                                    if (!ok) {
                                        return
                                    }
                                }

                                if (
                                    picturesToUpload.length !== 0 ||
                                    moviesToUpload.length !== 0
                                ) {
                                    handleUpload()
                                    return
                                }
                                navigate(
                                    genCleaningTourPagePath(
                                        cleaningId,
                                        `photo/item/${index + 1}`
                                    )
                                )
                            }}
                            variant="outlined"
                            color="primary"
                            endIcon={<ArrowForwardIcon />}
                        >
                            {t("進む")}
                        </Button>
                    </div>
                }
                progressBar={{
                    value: index + 1,
                    max: items?.length ?? 0
                }}
                page={{
                    title: item?.title ?? "",
                    description: item?.description ?? ""
                }}
                footer={
                    <footer
                        css={css`
                            position: fixed;
                            bottom: 0;
                            left: 0;
                            display: grid;
                            width: 100%;
                        `}
                    >
                        <Paper
                            elevation={12}
                            css={css`
                                padding: 12px 8px 34px 8px;
                            `}
                        >
                            <div
                                css={css`
                                    display: grid;
                                    grid-template-columns: 1fr 1fr;
                                    gap: 8px;
                                    width: 100%;
                                `}
                            >
                                <input
                                    type="file"
                                    multiple
                                    accept={["image/*", "video/*"].join(",")}
                                    ref={uploadFileInputRef}
                                    css={css`
                                        display: none;
                                    `}
                                    onChange={async event => {
                                        const fileList =
                                            event.currentTarget.files
                                        if (!fileList) {
                                            return
                                        }
                                        const files = Array.from(fileList)

                                        const images: Picture[] = []
                                        const movies: Movie[] = []

                                        files.forEach(file => {
                                            if (file.type.startsWith("image")) {
                                                images.push({
                                                    id: genId(),
                                                    file,
                                                    description: "",
                                                    status: "uploading"
                                                })
                                            } else if (
                                                file.type.startsWith("video")
                                            ) {
                                                movies.push({
                                                    id: genId(),
                                                    file,
                                                    description: "",
                                                    status: "uploading"
                                                })
                                            }
                                        })

                                        setPicturesToUpload(prev => [
                                            ...prev,
                                            ...images
                                        ])
                                        setMoviesToUpload(prev => [
                                            ...prev,
                                            ...movies
                                        ])

                                        // 同じファイルを再選択した際にも change を発火させるため input をリセットする
                                        event.currentTarget.value = ""

                                        // 選択と同時に PUT を先行実行する
                                        images.forEach(startImagePut)
                                        movies.forEach(startMoviePut)
                                    }}
                                />
                                <MobileFooterIconButton
                                    icon={<AddAPhotoIcon fontSize="large" />}
                                    label={t("写真を追加")}
                                    css={css`
                                        color: white;
                                        background-color: ${
                                            theme.palette.intentions.primary
                                                .main
                                        };
                                    `}
                                    onClick={() => {
                                        uploadFileInputRef.current?.click()
                                    }}
                                />
                                <MobileFooterIconButton
                                    icon={<UploadIcon fontSize="large" />}
                                    label={
                                        picturesToUpload.length +
                                            moviesToUpload.length >
                                        0
                                            ? t(
                                                  `送信 (${
                                                      picturesToUpload.length +
                                                      moviesToUpload.length
                                                  })`
                                              )
                                            : t("送信")
                                    }
                                    css={css`
                                        color: ${
                                            theme.palette.intentions.primary
                                                .main
                                        };
                                    `}
                                    onClick={handleUpload}
                                    disabled={waitingUpload}
                                />
                            </div>
                        </Paper>
                    </footer>
                }
            >
                <div
                    css={css`
                        display: grid;
                        gap: 8px;
                        padding-bottom: 155px; // Footer121px + iOSインジケーター34px
                        margin-top: 16px;
                    `}
                >
                    <Typography variant="h4">
                        {t("選択中の画像・動画")}
                    </Typography>

                    <div
                        css={css`
                            display: grid;
                            grid-template-columns: 1fr 1fr 1fr;
                            gap: 8px;
                        `}
                    >
                        {picturesToUpload.map(picture => {
                            return (
                                <div
                                    key={picture.id}
                                    css={styles.uploadedThumbnailWrapper}
                                >
                                    <Thumbnail
                                        src={picture.previewUrl}
                                        css={css`
                                            display: flex;
                                            width: 100%;
                                            aspect-ratio: 1;
                                            object-fit: cover;
                                        `}
                                    />
                                    <UploadStatusOverlay
                                        status={picture.status}
                                    />
                                    <button
                                        type="button"
                                        aria-label={t("写真を削除")}
                                        css={styles.deleteButton}
                                        disabled={waitingUpload}
                                        onClick={() =>
                                            removePicture(picture.id)
                                        }
                                    >
                                        <CloseIcon fontSize="small" />
                                    </button>
                                    <TextField
                                        label={t("説明")}
                                        value={picture.description}
                                        onChange={value =>
                                            updatePicture(picture.id, {
                                                description: value
                                            })
                                        }
                                    />
                                </div>
                            )
                        })}

                        {moviesToUpload.map(movie => {
                            return (
                                <div
                                    key={movie.id}
                                    css={styles.uploadedThumbnailWrapper}
                                >
                                    <VideoThumbnail
                                        file={movie.file}
                                        controls
                                        css={css`
                                            display: flex;
                                            width: 100%;
                                            aspect-ratio: 1;
                                            object-fit: cover;
                                        `}
                                    />
                                    <UploadStatusOverlay
                                        status={movie.status}
                                    />
                                    <button
                                        type="button"
                                        aria-label={t("動画を削除")}
                                        css={styles.deleteButton}
                                        disabled={waitingUpload}
                                        onClick={() => removeMovie(movie.id)}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </button>
                                    <TextField
                                        label={t("説明")}
                                        value={movie.description}
                                        onChange={value =>
                                            updateMovie(movie.id, {
                                                description: value
                                            })
                                        }
                                    />
                                </div>
                            )
                        })}
                    </div>

                    <Typography variant="h4">
                        {t("アップロード済みの画像")}
                    </Typography>
                    {filteredPictures && filteredPictures.length > 0 && (
                        <div
                            css={css`
                                display: grid;
                                grid-template-columns: repeat(3, 1fr);
                                gap: 8px;
                            `}
                        >
                            {filteredPictures.map(picture => {
                                const isDeleting = deletingImageIds.has(
                                    picture.id
                                )
                                return (
                                    <div
                                        key={picture.id}
                                        css={styles.uploadedThumbnailWrapper}
                                    >
                                        <Thumbnail
                                            src={picture.url}
                                            css={css`
                                                display: flex;
                                                width: 100%;
                                                aspect-ratio: 1;
                                                object-fit: cover;
                                            `}
                                        />
                                        <button
                                            type="button"
                                            aria-label={t("写真を削除")}
                                            css={styles.deleteButton}
                                            disabled={isDeleting}
                                            onClick={() =>
                                                handleDeleteImage(picture.id)
                                            }
                                        >
                                            <CloseIcon fontSize="small" />
                                        </button>
                                        <Typography variant="caption">
                                            {picture.description}
                                        </Typography>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                    <Typography variant="h4">
                        {t("アップロード済みの動画")}
                    </Typography>
                    {filteredMovies && filteredMovies.length > 0 && (
                        <div
                            css={css`
                                display: grid;
                                grid-template-columns: repeat(3, 1fr);
                                gap: 8px;
                            `}
                        >
                            {filteredMovies.map(movie => {
                                const isDeleting = deletingMovieIds.has(
                                    movie.id
                                )
                                return (
                                    <div
                                        key={movie.id}
                                        css={styles.uploadedThumbnailWrapper}
                                    >
                                        <VideoThumbnail
                                            src={movie.url}
                                            controls
                                            css={css`
                                                display: flex;
                                                width: 100%;
                                                aspect-ratio: 1;
                                                object-fit: cover;
                                            `}
                                        />
                                        <button
                                            type="button"
                                            aria-label={t("動画を削除")}
                                            css={styles.deleteButton}
                                            disabled={isDeleting}
                                            onClick={() =>
                                                handleDeleteMovie(movie.id)
                                            }
                                        >
                                            <CloseIcon fontSize="small" />
                                        </button>
                                        <Typography variant="caption">
                                            {movie.description}
                                        </Typography>
                                    </div>
                                )
                            })}
                        </div>
                    )}

                    {sampleImages && sampleImages.length > 0 && (
                        <>
                            <Typography variant="h4">
                                {t("参考画像")}
                            </Typography>
                            <div
                                css={css`
                                    display: grid;
                                    grid-template-columns: repeat(3, 1fr);
                                    gap: 8px;
                                `}
                            >
                                {sampleImages.map((sampleImage, index) => {
                                    return (
                                        <div key={index}>
                                            <Thumbnail
                                                src={sampleImage.url}
                                                css={css`
                                                    display: flex;
                                                    width: 100%;
                                                    aspect-ratio: 1;
                                                    object-fit: cover;
                                                `}
                                            />
                                            <Typography variant="caption">
                                                {sampleImage.description}
                                            </Typography>
                                        </div>
                                    )
                                })}
                            </div>
                        </>
                    )}
                </div>

                {waitingUpload && (
                    <div
                        css={css`
                            position: fixed;
                            top: 0;
                            left: 0;
                            display: grid;
                            place-items: center;
                            width: 100vw;
                            height: 100vh;
                            /* iOS対応 */
                            height: 100dvh;

                            background-color: rgba(0, 0, 0, 0.75);
                        `}
                    >
                        <div
                            css={css`
                                display: grid;
                                gap: 16px;
                                place-items: center;
                            `}
                        >
                            <Loading
                                css={css`
                                    font-size: 48px;
                                `}
                            />
                            <Typography
                                css={css`
                                    color: white;
                                `}
                            >
                                {t("送信しています…")} ({uploaded} / {uploading}
                                )
                            </Typography>
                        </div>
                    </div>
                )}
            </PhotoPageLayout>
        </>
    )
}

// indexが切り替わると別のページなのでページ全体を再レンダリングしてstateなどをリセットして欲しい
// 基本的にはコンポーネントを丸ごと再レンダリングするのでかまわないため、ここでindexをkeyとしてセットしている
export const ItemPage = () => {
    const { index: indexStr } = useParams<{ index: string }>()
    assertIsDefined(indexStr)
    const index = Number(indexStr)

    return <ItemPageInner key={index} index={index} />
}
